import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSseSubscription } from '../context/SseContext';
import NavHeader from '../components/NavHeader';
import ChecklistProgressBar from '../components/ChecklistProgressBar';
import PackageCard from '../components/PackageCard';
import {
  approveVatPackage,
  batchLuxembourg,
  submitLuxBatch,
  clearAllInvoices,
  createApprovalRequest,
  downloadVatBatch,
  fetchInvoices,
  getProjection,
  toggleLuxBatchChecklistItem,
  toggleVatBatchChecklistItem,
  updateInvoiceStatus,
  uploadInvoice,
} from '../api';

// ---------------------------------------------------------------------------
// Design tokens (ported from Claude Design bundle "Erbi Board Redesign.html")
// ---------------------------------------------------------------------------

const TOKENS = {
  bg:           'oklch(0.985 0.003 250)',
  panel:        'oklch(1 0 0)',
  panelAlt:     'oklch(0.975 0.004 250)',
  border:       'oklch(0.92 0.006 250)',
  borderStrong: 'oklch(0.86 0.008 250)',
  ink:          'oklch(0.18 0.012 260)',
  ink2:         'oklch(0.42 0.012 260)',
  ink3:         'oklch(0.62 0.010 255)',
  accent:       'oklch(0.55 0.15 260)',
  accentSoft:   'oklch(0.94 0.03 260)',
  select:       'oklch(0.55 0.15 260)',
  selectSoft:   'oklch(0.96 0.035 260)',
  danger:       'oklch(0.58 0.18 25)',
  warn:         'oklch(0.70 0.14 70)',
  ok:           'oklch(0.62 0.13 150)',
};

// Repointed to the shared Inter stack (src/typography.css). MONO_FONT is
// kept because the name is load-bearing across this file's inline styles.
const UI_FONT = 'var(--font-ui)';
const MONO_FONT = 'var(--font-ui)';

// Inline styles win over CSS classes, so amounts/identifiers spread these
// instead of using the .num/.code utility classes. Identifiers (invoice
// numbers) additionally get the slashed zero.
const NUM_STYLE = {
  fontFamily: 'var(--font-ui)',
  fontVariantNumeric: 'tabular-nums',
};
const CODE_STYLE = {
  ...NUM_STYLE,
  fontFeatureSettings: '"zero" 1',
};

const laneColor = (hue, l = 0.62, c = 0.14) => `oklch(${l} ${c} ${hue})`;
const laneSoft = (hue) => `oklch(0.96 0.03 ${hue})`;

// ---------------------------------------------------------------------------
// Lane model — the board ends at Approved / Rejected. Anything downstream
// (submitted_to_ap, payment_processing, posted_in_sap, reconciled, VAT/Lux
// pipelines) drops off the board.
// ---------------------------------------------------------------------------

const LANES = [
  { id: 'received',         name: 'Received',         hue: 222 },
  { id: 'under_review',     name: 'Under Review',     hue: 260 },
  { id: 'pending_approval', name: 'Pending Approval', hue: 40  },
  { id: 'approved',         name: 'Approved',         hue: 150, hasSubtypes: true },
  { id: 'rejected',         name: 'Rejected',         hue: 15  },
];

const APPROVAL_TYPES = [
  { id: 'standard',   short: 'Standard',   hue: 150 },
  { id: 'vat',        short: 'VAT',        hue: 290 },
  { id: 'luxembourg', short: 'Luxembourg', hue: 35  },
];

// Raw status strings that drop off the board entirely. Approved VAT /
// VAT / Luxembourg invoices stay on the board inside the Approved lane's
// VAT / Luxembourg tabs through the entire open + batched phase —
// they only fall off once the package is marked Sent to AP (vat_submitted
// / lux_submitted), at which point further work happens on the VAT /
// Luxembourg dashboards.
const OFF_BOARD_STATUSES = new Set([
  'submitted_to_ap', 'payment_processing', 'posted_in_sap', 'reconciled',
  'vat_submitted', 'vat_paid',
  'lux_submitted', 'lux_paid',
]);

// Statuses surfaced inside the Approved lane. `approved` is the Standard
// tab; `vat_open` / `vat_batched` invoices roll up into the VAT package
// cards (and must be filtered out of any flat per-invoice rendering inside
// Approved); `lux_open` / `lux_batched` likewise for Luxembourg.
const APPROVED_LANE_STATUSES = new Set([
  'approved',
  'vat_open', 'vat_batched',
  'lux_open', 'lux_batched',
]);

// Human labels for the projection's `vat_stage` field (the invoice's raw VAT
// status). Only cards that surface as ordinary invoices while sitting in the
// VAT pipeline ever carry one — i.e. the per-invoice-packages mode, where a
// batch holds a single invoice and the grouped package card would be pure
// noise. With grouped batches the projection leaves `vat_stage` off every
// standard card, so none of the stage UI below renders.
const VAT_STAGE_LABELS = {
  vat_open: 'Open',
  vat_batched: 'Batched',
  vat_submitted: 'Sent to AP',
  vat_paid: 'Paid',
};

function approvalTypeFor(inv) {
  if (inv.is_vat || inv.status === 'vat_open' || inv.status === 'vat_batched') return 'vat';
  if (inv.is_luxembourg || inv.status === 'lux_open' || inv.status === 'lux_batched') return 'luxembourg';
  return 'standard';
}

function formatMoney(amount, currency) {
  if (amount == null || amount === '') return '';
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return '';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

function SelectBox({ checked, onChange, size = 14 }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      style={{
        width: size,
        height: size,
        borderRadius: 3,
        flex: '0 0 auto',
        background: checked ? TOKENS.select : TOKENS.panel,
        border: `1.5px solid ${checked ? TOKENS.select : TOKENS.borderStrong}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        cursor: 'pointer',
        transition: 'all .12s',
      }}
    >
      {checked && (
        <svg
          width={size - 4}
          height={size - 4}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12l4 4 10-10" />
        </svg>
      )}
    </span>
  );
}

function CurrencyTag({ c }) {
  return (
    <span
      style={{
        fontFamily: MONO_FONT,
        fontSize: 9.5,
        color: TOKENS.ink3,
        letterSpacing: '0.04em',
      }}
    >
      {c || ''}
    </span>
  );
}

function ApprovalTypeChip({ typeId }) {
  const t = APPROVAL_TYPES.find((x) => x.id === typeId);
  if (!t) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: UI_FONT,
        fontSize: 9.5,
        fontWeight: 600,
        color: laneColor(t.hue, 0.38, 0.12),
        background: laneSoft(t.hue),
        border: `1px solid ${laneColor(t.hue, 0.84, 0.08)}`,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: '0.01em',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: laneColor(t.hue),
        }}
      />
      {t.short}
    </span>
  );
}

// Where in the VAT pipeline this invoice sits. Deliberately styled as a
// quieter sibling of ApprovalTypeChip (same VAT hue, no leading dot, outline
// rather than fill) so the pair reads as "VAT" + "…and it is Batched" rather
// than two competing badges.
function VatStagePill({ label }) {
  const hue = 290;
  return (
    <span
      title={`VAT stage: ${label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: UI_FONT,
        fontSize: 9.5,
        fontWeight: 600,
        color: laneColor(hue, 0.42, 0.1),
        background: TOKENS.panel,
        border: `1px solid ${laneColor(hue, 0.86, 0.06)}`,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: '0.01em',
      }}
    >
      {label}
    </span>
  );
}

// Download affordance for a card whose VAT package has already been built.
// Normally the package PDF is reached through the grouped PackageCard or the
// VAT dashboard; in per-invoice-packages mode neither is on the card's path,
// so the card carries the link itself. Mirrors the VAT dashboard's download
// flow (ask the API for a presigned URL, open it in a new tab) and keeps its
// own busy/failed state so no handler has to be threaded through the lanes.
function PackageDownloadLink({ batchId }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClick = async (e) => {
    // The whole card is clickable (opens invoice detail) — never let a
    // download click fall through to it.
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const res = await downloadVatBatch(batchId);
      const url = res.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={failed ? 'Download failed — try again' : 'Download the VAT package PDF'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: UI_FONT,
        fontSize: 9.5,
        fontWeight: 600,
        color: failed ? TOKENS.danger : TOKENS.ink2,
        background: TOKENS.panel,
        border: `1px solid ${failed ? TOKENS.danger : TOKENS.border}`,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: '0.01em',
        cursor: busy ? 'wait' : 'pointer',
      }}
    >
      <svg
        width="9"
        height="9"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3v12M7 11l5 5 5-5M4 20h16" />
      </svg>
      {busy ? 'Opening…' : failed ? 'Retry download' : 'Package'}
    </button>
  );
}

function ConfidenceBar({ value }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color =
    pct >= 90 ? TOKENS.ok : pct >= 75 ? TOKENS.warn : TOKENS.danger;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div
        style={{
          width: 32,
          height: 4,
          background: TOKENS.panelAlt,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{ width: `${pct}%`, height: '100%', background: color }}
        />
      </div>
      <span
        style={{
          ...NUM_STYLE,
          fontSize: 10,
          color: TOKENS.ink3,
        }}
      >
        {pct}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

// Stages a processing invoice walks through while extraction + classification
// run in the background. We don't get per-step events from the pipeline, so
// the timings are estimated from typical end-to-end durations. Math.max
// guarantees the stage only ever advances — a re-render with stale timers
// can never roll the label backward.
const PROCESSING_STAGES = [
  'Extracting fields…',
  'Analyzing content…',
  'Matching to project…',
];
const PROCESSING_STAGE_TIMINGS = [4500, 11000];

function ProcessingCard({ invoice }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = PROCESSING_STAGE_TIMINGS.map((delay, idx) =>
      setTimeout(() => setStage((s) => Math.max(s, idx + 1)), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div
      style={{
        width: '100%',
        background: TOKENS.panel,
        border: `1px dashed ${TOKENS.borderStrong}`,
        borderRadius: 8,
        padding: '12px 11px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: UI_FONT,
        color: TOKENS.ink2,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          border: `2px solid ${TOKENS.border}`,
          borderTopColor: TOKENS.accent,
          animation: 'erbi-spin 0.9s linear infinite',
          flex: '0 0 auto',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12.5, fontWeight: 550, color: TOKENS.ink }}>
          {PROCESSING_STAGES[stage]}
        </span>
        <span
          style={{
            ...CODE_STYLE,
            fontSize: 10.5,
            color: TOKENS.ink3,
          }}
        >
          {invoice.invoice_number || invoice.id?.slice(0, 8)}
        </span>
      </div>
    </div>
  );
}

function InvoiceCard({
  invoice,
  onClick,
  selectable,
  selected,
  onSelect,
  canSendForApproval,
  onSendForApproval,
  sendingApproval,
  showBatchLuxembourg,
  canBatchLuxembourg,
  onBatchLuxembourg,
  batchingLux,
  justArrived,
  laneHue,
}) {
  const extractionInFlight =
    invoice.extraction_status === 'pending' ||
    invoice.extraction_status === 'processing';
  if (extractionInFlight && !invoice.vendor_name) {
    return <ProcessingCard invoice={invoice} />;
  }

  const vendorName = invoice.vendor_name || 'Unknown vendor';
  const project = invoice.project_name;
  const typeId = approvalTypeFor(invoice);
  // VAT / Luxembourg invoices wear their pill in every lane so Sandra can
  // spot them at a glance no matter where they sit in the pipeline.
  // Standard approvals only reveal the pill inside the Approved lane's
  // sub-columns, where the split is otherwise the only visual signal.
  const showChip =
    typeId !== 'standard' || APPROVED_LANE_STATUSES.has(invoice.status);

  // Per-invoice VAT packages: a VAT invoice that would otherwise have rolled
  // up into a grouped package card is emitted as an ordinary card instead, so
  // the card has to carry the two things the package card used to provide —
  // where it is in the VAT pipeline, and the package PDF. Both source fields
  // are absent from every card the projection emits in grouped mode (and from
  // the flat /invoices list that feeds the pre-approval lanes), so this whole
  // block renders nothing there. `package_s3_key` is what says the PDF has
  // actually been built; `vat_batch_id` is what the download endpoint keys on,
  // so both are required before the link is offered.
  const vatStageLabel = VAT_STAGE_LABELS[invoice.vat_stage] || null;
  const packageBatchId = invoice.package_s3_key ? invoice.vat_batch_id : null;

  // Calm arrival highlight — when a card has just landed in a new lane (e.g.
  // an auto-routed invoice surfacing in Under Review or Pending Approval),
  // we run a one-shot 2.8s decay: thin colored ring + soft outer glow, both
  // tinted to match the destination lane. No scale, no bounce. The animation
  // is unique per card instance + status so React doesn't replay it on
  // unrelated re-renders.
  const arrivalKey = justArrived ? `${invoice.id}-${invoice.status}` : null;
  const accent = laneHue != null ? laneColor(laneHue) : TOKENS.accent;
  const accentSoft = laneHue != null ? laneSoft(laneHue) : TOKENS.accentSoft;
  const animationCss = arrivalKey
    ? `erbi-card-arrive 2800ms cubic-bezier(0.22, 1, 0.36, 1) both`
    : 'none';

  return (
    <div
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        background: selected ? TOKENS.selectSoft : TOKENS.panel,
        border: `1px solid ${selected ? TOKENS.select : TOKENS.border}`,
        boxShadow: selected
          ? `inset 0 0 0 1px ${TOKENS.select}`
          : 'none',
        borderRadius: 8,
        padding: '10px 11px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontFamily: UI_FONT,
        color: TOKENS.ink,
        transition: 'border-color .12s, background .12s, box-shadow .12s',
        animation: animationCss,
        // Inline custom properties so the keyframes can read the lane-tinted
        // colors. Avoids generating per-instance keyframes.
        '--erbi-arrive-accent': accent,
        '--erbi-arrive-accent-soft': accentSoft,
      }}
      // The data attribute changes every time the card transitions lanes,
      // forcing React to treat the animation as fresh on the new mount/render.
      data-arrival-key={arrivalKey || undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {selectable && (
          <SelectBox checked={!!selected} onChange={onSelect} />
        )}
        <div
          style={{
            fontWeight: 550,
            fontSize: 12.5,
            letterSpacing: '-0.005em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
          }}
        >
          {vendorName}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <CurrencyTag c={invoice.currency} />
        <span
          style={{
            ...NUM_STYLE,
            fontSize: 15,
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        >
          {formatMoney(invoice.amount, invoice.currency) || '—'}
        </span>
      </div>

      {showChip && <ApprovalTypeChip typeId={typeId} />}

      {(vatStageLabel || packageBatchId) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {vatStageLabel && <VatStagePill label={vatStageLabel} />}
          {packageBatchId && <PackageDownloadLink batchId={packageBatchId} />}
        </div>
      )}

      {invoice.package_checklist_progress && (
        <ChecklistProgressBar
          progress={invoice.package_checklist_progress}
          invoiceStatus={invoice.status}
        />
      )}

      {project && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: MONO_FONT,
              fontSize: 9.5,
              color: TOKENS.ink2,
              background: TOKENS.panelAlt,
              border: `1px solid ${TOKENS.border}`,
              padding: '1px 5px',
              borderRadius: 3,
            }}
          >
            {project}
          </span>
          {invoice.is_split && invoice.allocation_count > 1 && (
            <span
              title={`Split across ${invoice.allocation_count} projects`}
              style={{
                ...NUM_STYLE,
                fontSize: 9.5,
                color: '#7a4500',
                background: '#fff4d6',
                border: '1px solid #f0c36d',
                padding: '1px 5px',
                borderRadius: 3,
                fontWeight: 600,
              }}
            >
              split: {invoice.allocation_count}
            </span>
          )}
          {invoice.jurisdiction && (
            <span style={{ fontSize: 10.5, color: TOKENS.ink3 }}>
              {invoice.jurisdiction}
            </span>
          )}
        </div>
      )}

      {canSendForApproval && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSendForApproval(invoice);
          }}
          disabled={sendingApproval}
          style={{
            padding: '5px 8px',
            borderRadius: 6,
            border: `1px solid ${TOKENS.border}`,
            background: sendingApproval ? TOKENS.panelAlt : TOKENS.panel,
            color: sendingApproval ? TOKENS.ink3 : TOKENS.ink,
            fontFamily: UI_FONT,
            fontSize: 11,
            fontWeight: 550,
            cursor: sendingApproval ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            marginTop: 2,
          }}
          title={
            invoice.owner_name
              ? `Send to ${invoice.owner_name} for approval`
              : 'Send for approval'
          }
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
          Send for approval
          {invoice.owner_name && (
            <span style={{ fontWeight: 400, color: TOKENS.ink3 }}>
              · {invoice.owner_name}
            </span>
          )}
        </button>
      )}

      {showBatchLuxembourg && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (canBatchLuxembourg && !batchingLux) onBatchLuxembourg(invoice);
          }}
          disabled={!canBatchLuxembourg || batchingLux}
          style={{
            padding: '5px 8px',
            borderRadius: 6,
            border: `1px solid ${TOKENS.border}`,
            background: batchingLux ? TOKENS.panelAlt : TOKENS.panel,
            color: !canBatchLuxembourg || batchingLux ? TOKENS.ink3 : TOKENS.ink,
            fontFamily: UI_FONT,
            fontSize: 11,
            fontWeight: 550,
            cursor: !canBatchLuxembourg
              ? 'not-allowed'
              : batchingLux
              ? 'wait'
              : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            marginTop: 2,
          }}
          title={
            canBatchLuxembourg
              ? 'Confirm as Luxembourg and add to the quarterly batch'
              : 'Only Sandra can batch Luxembourg invoices'
          }
        >
          {batchingLux ? 'Batching...' : 'Batch as Luxembourg'}
        </button>
      )}

      {invoice.status === 'pending_approval' && invoice.owner_name && (
        <div
          style={{
            padding: '5px 8px',
            borderRadius: 6,
            border: `1px solid ${TOKENS.border}`,
            background: TOKENS.panelAlt,
            color: TOKENS.ink2,
            fontFamily: UI_FONT,
            fontSize: 11,
            fontWeight: 550,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            marginTop: 2,
          }}
          title={`Awaiting approval from ${invoice.owner_name}`}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          {invoice.owner_name}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 2,
        }}
      >
        <span
          style={{
            ...CODE_STYLE,
            fontSize: 9.5,
            color: TOKENS.ink3,
          }}
        >
          {invoice.invoice_number || ''}
        </span>
        <ConfidenceBar value={invoice.classification_confidence} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lane
// ---------------------------------------------------------------------------

function Lane({ lane, items, onCard, sendForApprovalContext, dropZone, recentlyMovedIds }) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const isDropZone = !!dropZone;

  const handleDragOver = (e) => {
    if (!isDropZone) return;
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    if (!isDropZone) return;
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };
  const handleDrop = (e) => {
    if (!isDropZone) return;
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) dropZone.onFiles(files);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: dragOver ? TOKENS.accentSoft : TOKENS.panelAlt,
        border: `${dragOver ? 2 : 1}px ${dragOver ? 'dashed' : 'solid'} ${
          dragOver ? TOKENS.accent : TOKENS.border
        }`,
        borderRadius: 10,
        padding: 8,
        position: 'relative',
        overflow: 'hidden',
        transition: 'background .12s, border-color .12s',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: laneColor(lane.hue),
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 4px 4px',
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: laneColor(lane.hue),
          }}
        />
        <span
          style={{
            fontFamily: UI_FONT,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: TOKENS.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {lane.name}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            ...NUM_STYLE,
            fontSize: 11,
            color: TOKENS.ink2,
            background: TOKENS.panel,
            border: `1px solid ${TOKENS.border}`,
            padding: '0 6px',
            borderRadius: 8,
          }}
        >
          {items.length}
        </span>
        {isDropZone && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) dropZone.onFiles(files);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={dropZone.uploading}
              title="Upload one or more invoice files"
              style={{
                marginLeft: 4,
                padding: '2px 7px',
                borderRadius: 6,
                background: TOKENS.accent,
                color: '#fff',
                border: 'none',
                fontFamily: UI_FONT,
                fontSize: 10.5,
                fontWeight: 500,
                cursor: dropZone.uploading ? 'wait' : 'pointer',
                opacity: dropZone.uploading ? 0.7 : 1,
              }}
            >
              {dropZone.uploading ? '…' : 'Upload'}
            </button>
          </>
        )}
      </div>
      {isDropZone && items.length === 0 && (
        <div
          style={{
            margin: '4px 2px 2px',
            padding: '14px 10px',
            borderRadius: 8,
            border: `1px dashed ${TOKENS.borderStrong}`,
            color: TOKENS.ink3,
            fontSize: 11.5,
            textAlign: 'center',
            lineHeight: 1.4,
            pointerEvents: 'none',
          }}
        >
          Drop PDFs or Excel files here
          <br />
          to ingest as invoices
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          overflowY: 'auto',
          flex: 1,
          paddingRight: 2,
        }}
      >
        {items.map((inv) => {
          const ctx = sendForApprovalContext || {};
          const inIntake =
            ctx.eligibleStatuses && ctx.eligibleStatuses.includes(inv.status);
          const isLux = !!inv.is_luxembourg;
          const canSend =
            inIntake && inv.owner_id && ctx.role === 'processor' && !isLux;
          const showBatchLux = isLux && inv.status === 'under_review';
          const canBatchLux =
            showBatchLux && ['processor', 'admin'].includes(ctx.role);
          return (
            <InvoiceCard
              key={inv.id}
              invoice={inv}
              onClick={() => onCard(inv)}
              canSendForApproval={canSend}
              onSendForApproval={ctx.onSend}
              sendingApproval={ctx.pending === inv.id}
              showBatchLuxembourg={showBatchLux}
              canBatchLuxembourg={canBatchLux}
              onBatchLuxembourg={ctx.onBatchLux}
              batchingLux={ctx.batchingLux === inv.id}
              justArrived={recentlyMovedIds?.has(inv.id)}
              laneHue={lane.hue}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending Approval lane — three tabs (Standard / VAT Batching / Luxembourg
// Batching). Standard tab shows individual invoice cards (current UX);
// VAT/Lux tabs show grouped PackageCard rows sourced from the kanban
// projection's `pending_approval.{vat,lux}_packages` arrays.
//
// Standard pending invoices that are NOT attached to any package are still
// passed in via `standardItems`. Pending invoices that ARE in a package
// must be filtered out by the parent (they surface only inside their
// package card, never as top-level cards).
// ---------------------------------------------------------------------------

function PendingApprovalLane({
  lane,
  standardItems,
  vatPackages,
  luxPackages,
  childInvoiceLookup, // (childId) => invoice card, for expanded rows
  onCard,
  recentlyMovedIds,
  sendForApprovalContext,
  onToggleVatChecklistItem,
  onToggleLuxChecklistItem,
  onApproveVatPackage,
  onApproveLuxPackage,
}) {
  const TABS = [
    { id: 'standard', label: 'Standard',         hue: 40,  count: standardItems.length },
    { id: 'vat',      label: 'VAT Batching',     hue: 290, count: vatPackages.length   },
    { id: 'lux',      label: 'Luxembourg Batching', hue: 35, count: luxPackages.length },
  ];
  const [activeTab, setActiveTab] = useState('standard');

  const totalCount =
    standardItems.length + vatPackages.length + luxPackages.length;

  return (
    <div
      style={{
        // Pending Approval needs roomier columns than the other top-level
        // lanes because each tab body holds either invoice cards (Standard)
        // or rich PackageCards (VAT/Lux) with checklists, expanded child
        // rows, and an approve action. Match the Approved lane's flex budget
        // so the pending lane gets comparable horizontal real estate.
        flex: 2.4,
        minWidth: 220,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: TOKENS.panelAlt,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 10,
        padding: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: laneColor(lane.hue),
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 4px 4px',
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: laneColor(lane.hue),
          }}
        />
        <span
          style={{
            fontFamily: UI_FONT,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: TOKENS.ink,
          }}
        >
          {lane.name}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            ...NUM_STYLE,
            fontSize: 11,
            color: TOKENS.ink2,
            background: TOKENS.panel,
            border: `1px solid ${TOKENS.border}`,
            padding: '0 6px',
            borderRadius: 8,
          }}
        >
          {totalCount}
        </span>
      </div>

      {/* Tab strip */}
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 2,
          padding: '0 2px',
          borderBottom: `1px solid ${TOKENS.border}`,
        }}
      >
        {TABS.map((t) => {
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '6px 9px 7px',
                border: 'none',
                cursor: 'pointer',
                background: 'transparent',
                borderBottom: `2px solid ${
                  isActive ? laneColor(t.hue) : 'transparent'
                }`,
                marginBottom: -1,
                fontFamily: UI_FONT,
                fontSize: 11,
                fontWeight: isActive ? 600 : 500,
                color: isActive
                  ? laneColor(t.hue, 0.32, 0.12)
                  : TOKENS.ink2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: laneColor(t.hue),
                }}
              />
              {t.label}
              <span
                style={{
                  ...NUM_STYLE,
                  fontSize: 9.5,
                  color: TOKENS.ink3,
                  background: TOKENS.panel,
                  border: `1px solid ${TOKENS.border}`,
                  padding: '0 5px',
                  borderRadius: 6,
                }}
              >
                {t.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active tab body */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          overflowY: 'auto',
          flex: 1,
          paddingRight: 2,
        }}
      >
        {activeTab === 'standard' && (
          <>
            {standardItems.length === 0 ? (
              <div
                style={{
                  padding: '14px 8px',
                  fontFamily: UI_FONT,
                  fontSize: 11,
                  color: TOKENS.ink3,
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                No standard invoices awaiting approval.
              </div>
            ) : (
              standardItems.map((inv) => {
                const ctx = sendForApprovalContext || {};
                const inIntake =
                  ctx.eligibleStatuses &&
                  ctx.eligibleStatuses.includes(inv.status);
                const isLux = !!inv.is_luxembourg;
                const canSend =
                  inIntake && inv.owner_id && ctx.role === 'processor' && !isLux;
                const showBatchLux = isLux && inv.status === 'under_review';
                const canBatchLux =
                  showBatchLux && ['processor', 'admin'].includes(ctx.role);
                return (
                  <InvoiceCard
                    key={inv.id}
                    invoice={inv}
                    onClick={() => onCard(inv)}
                    canSendForApproval={canSend}
                    onSendForApproval={ctx.onSend}
                    sendingApproval={ctx.pending === inv.id}
                    showBatchLuxembourg={showBatchLux}
                    canBatchLuxembourg={canBatchLux}
                    onBatchLuxembourg={ctx.onBatchLux}
                    batchingLux={ctx.batchingLux === inv.id}
                    justArrived={recentlyMovedIds?.has(inv.id)}
                    laneHue={lane.hue}
                  />
                );
              })
            )}
          </>
        )}

        {activeTab === 'vat' && (
          <>
            {vatPackages.length === 0 ? (
              <div
                style={{
                  padding: '14px 8px',
                  fontFamily: UI_FONT,
                  fontSize: 11,
                  color: TOKENS.ink3,
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                No VAT packages awaiting approval.
              </div>
            ) : (
              vatPackages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  package={pkg}
                  kind="vat"
                  childInvoices={(pkg.child_invoice_ids || [])
                    .map(childInvoiceLookup)
                    .filter(Boolean)}
                  onToggleChecklistItem={(itemId, next) =>
                    onToggleVatChecklistItem(pkg.id, itemId, next)
                  }
                  onApprove={(batchId) => onApproveVatPackage(batchId)}
                  onChildClick={onCard}
                />
              ))
            )}
          </>
        )}

        {activeTab === 'lux' && (
          <>
            {luxPackages.length === 0 ? (
              <div
                style={{
                  padding: '14px 8px',
                  fontFamily: UI_FONT,
                  fontSize: 11,
                  color: TOKENS.ink3,
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                No Luxembourg packages awaiting approval.
              </div>
            ) : (
              luxPackages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  package={pkg}
                  kind="luxembourg"
                  childInvoices={(pkg.child_invoice_ids || [])
                    .map(childInvoiceLookup)
                    .filter(Boolean)}
                  onToggleChecklistItem={(itemId, next) =>
                    onToggleLuxChecklistItem(pkg.id, itemId, next)
                  }
                  onApprove={(batchId) => onApproveLuxPackage(batchId)}
                  onChildClick={onCard}
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approved lane — single unified stream of standard approved invoices and
// VAT / Luxembourg package cards. Tabs were dropped (issue #23 follow-up):
// once an item is approved, the Standard / VAT / Lux split adds noise more
// than signal — Sandra's job at this stage is to scan the queue and ship,
// not to filter by sub-type. Cards are sorted newest-first by their
// arrival timestamp (`oldest_invoice_received_at` for packages,
// `created_at` for individual invoices) so the most recent activity floats
// to the top of the lane.
//
// Standard invoice cards remain selectable and drive the bulk Send-to-AP
// bar. Package cards render in mode='approved' (read-only checklist, no
// Approve button — just an informational status line).
//
// The post-approval VAT/Lux invoices are surfaced exclusively via their
// package cards; the projection (`approved.standard`) already excludes
// them so no extra filtering is needed here.
// ---------------------------------------------------------------------------

function ApprovedLane({
  lane,
  standardItems,
  vatPackages,
  luxPackages,
  childInvoiceLookup,
  selection,
  toggleSelect,
  selectAllStandard,
  onCard,
  recentlyMovedIds,
  onToggleVatChecklistItem,
  onToggleLuxChecklistItem,
}) {
  const totalCount =
    standardItems.length + vatPackages.length + luxPackages.length;

  // Standard cards that the bulk Send-to-AP bar can actually act on. In
  // per-invoice-packages mode a VAT invoice sits in this lane as an ordinary
  // card while its status is already vat_open / vat_batched, and the state
  // machine only allows approved → submitted_to_ap — bulk-sending one would
  // be rejected by the API. Those cards therefore render without a checkbox;
  // their route to AP is the VAT dashboard. In grouped mode no card here
  // carries `vat_stage`, so this is every standard item, exactly as before.
  const bulkSelectable = standardItems.filter((i) => !i.vat_stage);

  const allStandardChecked =
    bulkSelectable.length > 0 && bulkSelectable.every((i) => selection[i.id]);

  // Build a single newest-first stream. Tag every entry with its kind so
  // the render switch stays readable, and use the appropriate timestamp
  // field as the sort key. Falls back to empty string so missing dates sort
  // last (after all dated items, since '' < any ISO timestamp).
  const unifiedStream = useMemo(() => {
    const entries = [];
    standardItems.forEach((inv) => {
      entries.push({
        kind: 'invoice',
        id: inv.id,
        sortKey: inv.created_at || '',
        payload: inv,
      });
    });
    vatPackages.forEach((pkg) => {
      entries.push({
        kind: 'vat',
        id: pkg.id,
        sortKey: pkg.oldest_invoice_received_at || '',
        payload: pkg,
      });
    });
    luxPackages.forEach((pkg) => {
      entries.push({
        kind: 'lux',
        id: pkg.id,
        sortKey: pkg.oldest_invoice_received_at || '',
        payload: pkg,
      });
    });
    // Newest-first: descending by ISO string compares correctly. Stable
    // tiebreak on id keeps render order deterministic across re-renders.
    entries.sort((a, b) => {
      if (b.sortKey !== a.sortKey) return b.sortKey > a.sortKey ? 1 : -1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
    return entries;
  }, [standardItems, vatPackages, luxPackages]);

  return (
    <div
      style={{
        // Match Pending Approval's flex budget so both rich lanes
        // get comparable horizontal real estate.
        flex: 2.4,
        minWidth: 220,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: TOKENS.panelAlt,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 10,
        padding: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: laneColor(lane.hue),
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 4px 4px',
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: laneColor(lane.hue),
          }}
        />
        <span
          style={{
            fontFamily: UI_FONT,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: TOKENS.ink,
          }}
        >
          {lane.name}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            ...NUM_STYLE,
            fontSize: 11,
            color: TOKENS.ink2,
            background: TOKENS.panel,
            border: `1px solid ${TOKENS.border}`,
            padding: '0 6px',
            borderRadius: 8,
          }}
        >
          {totalCount}
        </span>
      </div>

      {/* Unified body — no tabs. Standard invoice cards interleave with
          VAT / Lux package cards, sorted newest-first. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          overflowY: 'auto',
          flex: 1,
          paddingRight: 2,
        }}
      >
        {bulkSelectable.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 4px 2px',
            }}
          >
            <SelectBox
              checked={allStandardChecked}
              onChange={() => selectAllStandard(!allStandardChecked)}
              size={12}
            />
            <span
              style={{
                fontFamily: UI_FONT,
                fontSize: 10.5,
                color: TOKENS.ink3,
              }}
            >
              {allStandardChecked
                ? 'Unselect all standard'
                : 'Select all standard'}
            </span>
          </div>
        )}

        {unifiedStream.length === 0 ? (
          <div
            style={{
              padding: '14px 8px',
              fontFamily: UI_FONT,
              fontSize: 11,
              color: TOKENS.ink3,
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            Nothing approved yet.
          </div>
        ) : (
          unifiedStream.map((entry) => {
            if (entry.kind === 'invoice') {
              const inv = entry.payload;
              return (
                <InvoiceCard
                  key={`inv-${inv.id}`}
                  invoice={inv}
                  selectable={!inv.vat_stage}
                  selected={!!selection[inv.id]}
                  onSelect={() => toggleSelect(inv.id)}
                  onClick={() => onCard(inv)}
                  justArrived={recentlyMovedIds?.has(inv.id)}
                  laneHue={lane.hue}
                />
              );
            }
            if (entry.kind === 'vat') {
              const pkg = entry.payload;
              return (
                <PackageCard
                  key={`vat-${pkg.id}`}
                  package={pkg}
                  kind="vat"
                  mode="approved"
                  childInvoices={(pkg.child_invoice_ids || [])
                    .map(childInvoiceLookup)
                    .filter(Boolean)}
                  onToggleChecklistItem={(itemId, next) =>
                    onToggleVatChecklistItem(pkg.id, itemId, next)
                  }
                  onChildClick={onCard}
                />
              );
            }
            // entry.kind === 'lux'
            const pkg = entry.payload;
            return (
              <PackageCard
                key={`lux-${pkg.id}`}
                package={pkg}
                kind="luxembourg"
                mode="approved"
                childInvoices={(pkg.child_invoice_ids || [])
                  .map(childInvoiceLookup)
                  .filter(Boolean)}
                onToggleChecklistItem={(itemId, next) =>
                  onToggleLuxChecklistItem(pkg.id, itemId, next)
                }
                onChildClick={onCard}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload zone — moved into the Received lane (see Lane component above).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Bulk action bar — floats at the bottom while any approved cards are selected
// ---------------------------------------------------------------------------

function BulkActionBar({
  totalCount,
  standardCount,
  vatCount,
  luxCount,
  totalAmount,
  currency,
  onClear,
  onSend,
  busy,
  onOpenVatDashboard,
  onOpenLuxDashboard,
}) {
  if (totalCount === 0) return null;

  const hasSpecialized = vatCount > 0 || luxCount > 0;

  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 22,
        transform: 'translateX(-50%)',
        zIndex: 40,
        background: TOKENS.ink,
        color: '#fff',
        borderRadius: 10,
        padding: '10px 12px 10px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        fontFamily: UI_FONT,
        fontSize: 12,
        boxShadow:
          '0 18px 48px rgba(10,12,20,0.35), 0 2px 4px rgba(10,12,20,0.25)',
        minWidth: 460,
        maxWidth: 640,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: 'oklch(0.28 0.014 260)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...NUM_STYLE,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {totalCount}
          </span>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 1.2,
            }}
          >
            <span style={{ fontWeight: 550 }}>
              {totalCount} invoice{totalCount === 1 ? '' : 's'} selected
            </span>
            <span
              style={{
                fontSize: 10.5,
                color: 'oklch(0.70 0.012 260)',
                fontFamily: MONO_FONT,
                letterSpacing: '0.02em',
              }}
            >
              {standardCount > 0 && `${standardCount} standard`}
              {standardCount > 0 && (vatCount > 0 || luxCount > 0) && ' · '}
              {vatCount > 0 && `${vatCount} VAT`}
              {vatCount > 0 && luxCount > 0 && ' · '}
              {luxCount > 0 && `${luxCount} Luxembourg`}
            </span>
          </div>
        </div>

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <button
            onClick={onClear}
            disabled={busy}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid oklch(0.32 0.012 260)',
              background: 'transparent',
              color: 'oklch(0.80 0.006 250)',
              fontFamily: UI_FONT,
              fontSize: 11.5,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Clear
          </button>
          <button
            onClick={onSend}
            disabled={busy || standardCount === 0}
            style={{
              padding: '7px 14px',
              borderRadius: 6,
              border: 'none',
              background: standardCount === 0 ? 'oklch(0.32 0.012 260)' : TOKENS.ok,
              color: standardCount === 0 ? 'oklch(0.60 0.012 260)' : '#0a1510',
              fontFamily: UI_FONT,
              fontSize: 12,
              fontWeight: 600,
              cursor: busy
                ? 'wait'
                : standardCount === 0
                ? 'not-allowed'
                : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              opacity: busy ? 0.7 : 1,
            }}
            title={
              standardCount === 0
                ? 'Only standard invoices can be sent from here. VAT and Luxembourg packages are submitted from the package checklist (click the bar on a card) or the VAT/Luxembourg dashboard.'
                : `Mark ${standardCount} standard invoice${
                    standardCount === 1 ? '' : 's'
                  } as Sent to AP`
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12l4 4 10-10" />
            </svg>
            {busy
              ? 'Marking…'
              : standardCount === 0
              ? 'Mark as Sent to AP'
              : `Mark ${standardCount} as Sent to AP`}
          </button>
        </div>
      </div>

      {hasSpecialized && (
        <div
          style={{
            borderTop: '1px solid oklch(0.28 0.014 260)',
            paddingTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {vatCount > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: 11.5,
                color: 'oklch(0.85 0.012 260)',
                lineHeight: 1.4,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: laneColor(290),
                  marginTop: 6,
                  flex: '0 0 auto',
                }}
              />
              <div style={{ flex: 1 }}>
                <strong>{vatCount} VAT</strong> — complete the checklist
                and generate the package, then mark it as Sent to AP from
                the checklist popup or the VAT Dashboard.
              </div>
              <button
                onClick={onOpenVatDashboard}
                style={{
                  padding: '3px 8px',
                  borderRadius: 5,
                  border: '1px solid oklch(0.32 0.012 260)',
                  background: 'transparent',
                  color: '#fff',
                  fontFamily: UI_FONT,
                  fontSize: 11,
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                VAT Dashboard →
              </button>
            </div>
          )}
          {luxCount > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                fontSize: 11.5,
                color: 'oklch(0.85 0.012 260)',
                lineHeight: 1.4,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: laneColor(35),
                  marginTop: 6,
                  flex: '0 0 auto',
                }}
              />
              <div style={{ flex: 1 }}>
                <strong>{luxCount} Luxembourg</strong> — complete the
                checklist and generate the package, then mark it as Sent
                to AP from the checklist popup or the Luxembourg Dashboard.
              </div>
              <button
                onClick={onOpenLuxDashboard}
                style={{
                  padding: '3px 8px',
                  borderRadius: 5,
                  border: '1px solid oklch(0.32 0.012 260)',
                  background: 'transparent',
                  color: '#fff',
                  fontFamily: UI_FONT,
                  fontSize: 11,
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                Luxembourg Dashboard →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SentToast({ toast, onDismiss }) {
  if (!toast) return null;
  const { count, total, currency } = toast;
  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 22,
        transform: 'translateX(-50%)',
        zIndex: 40,
        background: TOKENS.ok,
        color: '#062015',
        borderRadius: 10,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: UI_FONT,
        fontSize: 12,
        boxShadow: '0 18px 48px rgba(10,40,25,0.35)',
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12l4 4 10-10" />
      </svg>
      <span style={{ fontWeight: 600 }}>
        {count} invoice{count === 1 ? '' : 's'} marked as Sent to AP
      </span>
      <span
        style={{
          fontSize: 10.5,
          ...NUM_STYLE,
          opacity: 0.7,
        }}
      >
        {currency} {total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </span>
      <button
        onClick={onDismiss}
        style={{
          marginLeft: 8,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#062015',
          padding: 2,
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete-all-invoices button — lives in the top-right of the global header.
// Visually shouts "destructive" because it wipes the entire database. This
// only exists during testing as a QA-reset hatch; remove before GA.
// ---------------------------------------------------------------------------

function DeleteAllInvoicesButton({ onClick, busy }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title="Delete every invoice — wipes all cards from the board. QA reset only."
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 11px',
        borderRadius: 6,
        background: 'oklch(0.30 0.13 25)',
        color: '#fff',
        border: '1px solid oklch(0.50 0.18 25)',
        fontFamily: UI_FONT,
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: '0.01em',
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.7 : 1,
        boxShadow: busy
          ? 'none'
          : '0 0 0 1px oklch(0.58 0.18 25 / 0.25), 0 1px 2px rgba(0,0,0,0.25)',
      }}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
      </svg>
      {busy ? 'Deleting…' : 'Delete all invoices'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main board
// ---------------------------------------------------------------------------

function KanbanBoard() {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [invoices, setInvoices] = useState([]);
  // Pending Approval package summaries from the kanban projection — these
  // are not surfaced via the flat /invoices endpoint, so we fetch the
  // projection in parallel. Defaults keep the rest of the board working
  // even if the projection load fails.
  const [vatPackages, setVatPackages] = useState([]);
  const [luxPackages, setLuxPackages] = useState([]);
  // Approved lane package summaries (Wave 5A) — same shape as the pending
  // ones but their children are post-approval (vat_open/vat_batched and
  // lux_open/lux_batched). Empty defaults keep the lane functional even if
  // the projection hasn't been redeployed yet.
  const [approvedVatPackages, setApprovedVatPackages] = useState([]);
  const [approvedLuxPackages, setApprovedLuxPackages] = useState([]);
  const [approvedStandardCards, setApprovedStandardCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selection, setSelection] = useState({});
  const [busyBulk, setBusyBulk] = useState(false);
  const [sentToast, setSentToast] = useState(null);
  const [errorToast, setErrorToast] = useState(null);
  const [sendingApprovalId, setSendingApprovalId] = useState(null);
  const [batchingLuxId, setBatchingLuxId] = useState(null);
  const [clearing, setClearing] = useState(false);

  // Track which cards have just transitioned status so we can surface a brief
  // arrival highlight on them. The set is keyed by invoice id; a per-id
  // timeout removes the id after the animation runs (2.8s + a small buffer).
  // previousStatusRef is the source of truth for "what status did this card
  // have on the last render?" — diffing against the new fetch tells us
  // exactly which cards transitioned.
  const previousStatusRef = useRef(null);
  const arrivalTimersRef = useRef(new Map());
  const [recentlyMovedIds, setRecentlyMovedIds] = useState(() => new Set());

  const loadData = useCallback(async () => {
    try {
      // Fire the flat-invoices and kanban-projection requests in parallel.
      // The flat list still drives every lane EXCEPT Pending Approval's
      // VAT/Lux tabs, which are sourced from the projection's package
      // summaries. A projection failure is logged but does not block the
      // board from rendering — the VAT/Lux tabs will simply show empty.
      const [invRes, projRes] = await Promise.allSettled([
        fetchInvoices(),
        getProjection('kanban_board'),
      ]);

      if (invRes.status === 'fulfilled') {
        const data = invRes.value.data;
        const rows = Array.isArray(data) ? data : data?.results ?? [];
        setInvoices(rows);
        setError(null);
      } else {
        const err = invRes.reason;
        const detail =
          err?.response?.data?.detail || err?.message || 'Unknown error';
        setError(`Failed to load data: ${detail}`);
      }

      if (projRes.status === 'fulfilled') {
        const payload = projRes.value?.data?.payload || {};
        const pa = payload.pending_approval || {};
        setVatPackages(Array.isArray(pa.vat_packages) ? pa.vat_packages : []);
        setLuxPackages(Array.isArray(pa.lux_packages) ? pa.lux_packages : []);
        const ap = payload.approved || {};
        setApprovedVatPackages(
          Array.isArray(ap.vat_packages) ? ap.vat_packages : []
        );
        setApprovedLuxPackages(
          Array.isArray(ap.lux_packages) ? ap.lux_packages : []
        );
        setApprovedStandardCards(
          Array.isArray(ap.standard) ? ap.standard : []
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn('Kanban projection load failed', projRes.reason);
        setVatPackages([]);
        setLuxPackages([]);
        setApprovedVatPackages([]);
        setApprovedLuxPackages([]);
        setApprovedStandardCards([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Detect status transitions across reloads so arrived cards get a brief
  // highlight. We diff the new invoice list against a ref of the previous
  // statuses — invoices whose status changed (and only those) get added to
  // recentlyMovedIds for ~3s, after which a timer removes them.
  useEffect(() => {
    const previous = previousStatusRef.current;
    const nextSnapshot = {};
    invoices.forEach((inv) => {
      nextSnapshot[inv.id] = inv.status;
    });

    if (previous !== null) {
      const transitioned = [];
      invoices.forEach((inv) => {
        const prevStatus = previous[inv.id];
        if (prevStatus !== undefined && prevStatus !== inv.status) {
          transitioned.push(inv.id);
        }
      });

      if (transitioned.length) {
        setRecentlyMovedIds((current) => {
          const next = new Set(current);
          transitioned.forEach((id) => next.add(id));
          return next;
        });
        transitioned.forEach((id) => {
          // If a card transitions twice in quick succession, replace any
          // pending timer so the second transition gets its full window.
          const existing = arrivalTimersRef.current.get(id);
          if (existing) clearTimeout(existing);
          const handle = setTimeout(() => {
            arrivalTimersRef.current.delete(id);
            setRecentlyMovedIds((current) => {
              if (!current.has(id)) return current;
              const next = new Set(current);
              next.delete(id);
              return next;
            });
          }, 3000);
          arrivalTimersRef.current.set(id, handle);
        });
      }
    }

    previousStatusRef.current = nextSnapshot;
  }, [invoices]);

  // Cleanup any pending arrival timers on unmount.
  useEffect(() => {
    const timers = arrivalTimersRef.current;
    return () => {
      timers.forEach((handle) => clearTimeout(handle));
      timers.clear();
    };
  }, []);

  // SSE — refresh the board on every stream message via the shared connection.
  useSseSubscription(() => loadData());

  // Drop invoices that have advanced past the board scope.
  const onBoard = invoices.filter(
    (inv) => !OFF_BOARD_STATUSES.has(inv.status)
  );

  // The kanban projection emits VAT/Lux pending packages with their
  // child_invoice_ids. Those children also live in the flat invoice list
  // (status=pending_approval). When rendering the Pending Approval lane
  // we must NOT also surface them as top-level cards — they belong to
  // their package. Build a Set of all package-child IDs so we can filter.
  const packageChildIds = new Set();
  vatPackages.forEach((p) => (p.child_invoice_ids || []).forEach((id) => packageChildIds.add(id)));
  luxPackages.forEach((p) => (p.child_invoice_ids || []).forEach((id) => packageChildIds.add(id)));

  // Same idea applies to the Approved lane (VAT/Lux invoices in
  // vat_open / vat_batched / lux_open / lux_batched roll up into the
  // Approved-lane package cards), but we lean on the projection to
  // exclude them from `approved.standard` rather than filtering here.

  // Lookup map for child rendering inside expanded package cards.
  // Includes both the on-board flat list (so VAT/Lux post-approval children
  // resolve) and the projection's approved.standard cards (covers cases
  // where the flat fetch hasn't caught up yet).
  const invoiceById = new Map(onBoard.map((inv) => [inv.id, inv]));
  approvedStandardCards.forEach((c) => {
    if (c?.id && !invoiceById.has(c.id)) invoiceById.set(c.id, c);
  });
  const childInvoiceLookup = (id) => invoiceById.get(id) || null;

  const invoicesByLane = {};
  LANES.forEach((l) => {
    if (l.id === 'approved') {
      // Approved-lane standard cards are now driven by the projection's
      // approved.standard slot. The flat list still contributes to the
      // VAT/Lux post-approval *children* (via the lookup above), but
      // those render only inside their Approved-lane package cards.
      invoicesByLane[l.id] = approvedStandardCards;
    } else if (l.id === 'pending_approval') {
      // Standard pending: pending_approval invoices NOT linked to a package.
      invoicesByLane[l.id] = onBoard.filter(
        (inv) => inv.status === l.id && !packageChildIds.has(inv.id)
      );
    } else {
      invoicesByLane[l.id] = onBoard.filter((inv) => inv.status === l.id);
    }
  });

  // Standard approved items used by the bulk Send-to-AP bar. Sourced from
  // the projection so it stays in sync with what the Approved tab renders.
  const approvedStandardItems = invoicesByLane.approved;

  // ----- Package checklist & approval handlers -----
  // Optimistic updates on the package list: flip the local item, then
  // POST. On failure the catch block re-flips so the bar matches reality.
  // SSE will reconcile the rest (e.g., the projection re-emits with the
  // server's authoritative state on next change).

  // Optimistic-flip helper that takes a setter and rewrites the matching
  // package's checklist item. Used for both pending and approved package
  // state slots so a checklist toggle in either lane stays consistent.
  const flipChecklistItem = (setter, batchId, itemId, value) => {
    setter((pkgs) =>
      pkgs.map((p) => {
        if (p.id !== batchId) return p;
        const items = (p.checklist?.items || []).map((it) =>
          it.id === itemId ? { ...it, completed: value } : it
        );
        const items_completed = items.filter((it) => it.completed).length;
        return {
          ...p,
          checklist: { ...p.checklist, items, items_completed },
        };
      })
    );
  };

  const toggleVatChecklist = useCallback(
    async (batchId, itemId, nextValue) => {
      flipChecklistItem(setVatPackages, batchId, itemId, nextValue);
      flipChecklistItem(setApprovedVatPackages, batchId, itemId, nextValue);
      try {
        await toggleVatBatchChecklistItem(batchId, itemId, nextValue);
      } catch (err) {
        flipChecklistItem(setVatPackages, batchId, itemId, !nextValue);
        flipChecklistItem(setApprovedVatPackages, batchId, itemId, !nextValue);
        throw err;
      }
    },
    []
  );

  const toggleLuxChecklist = useCallback(
    async (batchId, itemId, nextValue) => {
      flipChecklistItem(setLuxPackages, batchId, itemId, nextValue);
      flipChecklistItem(setApprovedLuxPackages, batchId, itemId, nextValue);
      try {
        await toggleLuxBatchChecklistItem(batchId, itemId, nextValue);
      } catch (err) {
        flipChecklistItem(setLuxPackages, batchId, itemId, !nextValue);
        flipChecklistItem(setApprovedLuxPackages, batchId, itemId, !nextValue);
        throw err;
      }
    },
    []
  );

  // Approve handlers: rely on SSE to refresh the board after success.
  // Errors propagate up so the PackageCard can surface them inline.
  const approveVat = useCallback(async (batchId) => {
    await approveVatPackage(batchId);
    loadData();
  }, [loadData]);

  // Luxembourg: the "Send to AP" action (gated by the package checklist)
  // submits the batch → invoices go lux_submitted and drop off the board.
  // (Lux has no per-invoice financial approval — see confirm-gate flow.)
  const sendLuxToAp = useCallback(async (batchId) => {
    await submitLuxBatch(batchId);
    loadData();
  }, [loadData]);

  // ----- Selection helpers -----
  // Only standard approved invoices are selectable now — VAT/Lux are
  // surfaced as package cards in the Approved lane and their downstream
  // actions (wire request, Cat B form, mark Sent to AP) live on the
  // dedicated dashboards rather than via the bulk bar.

  // A card carrying a `vat_stage` is an in-pipeline VAT invoice surfaced as an
  // ordinary card (per-invoice-packages mode). It is not bulk-sendable —
  // approved → submitted_to_ap is the only transition the state machine
  // allows, so the API would reject it. Guarded here as well as in the lane so
  // no path (select-all, a stale selection entry from before the invoice
  // advanced) can slip one into the bulk send. Grouped mode never sets the
  // field, so all three helpers behave exactly as before.
  const isBulkSendable = (inv) => !!inv && !inv.vat_stage;

  const toggleSelect = (id) => {
    const inv = approvedStandardItems.find((x) => x.id === id);
    if (!isBulkSendable(inv)) return;
    setSelection((s) => {
      const next = { ...s };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const selectAllStandardApproved = (on) => {
    setSelection((s) => {
      const next = { ...s };
      approvedStandardItems.filter(isBulkSendable).forEach((i) => {
        if (on) next[i.id] = true;
        else delete next[i.id];
      });
      return next;
    });
  };

  const selectedInvoices = Object.keys(selection)
    .map((id) => approvedStandardItems.find((x) => x.id === id))
    .filter(isBulkSendable);
  const standardSelected = selectedInvoices;
  const vatSelected = [];
  const luxSelected = [];
  const dominantCurrency = standardSelected[0]?.currency || 'EUR';
  const standardTotal = standardSelected
    .filter((i) => (i.currency || 'EUR') === dominantCurrency)
    .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const handleSend = async () => {
    if (standardSelected.length === 0) return;
    const standardIds = standardSelected.map((i) => i.id);
    setBusyBulk(true);
    try {
      // Send SEQUENTIALLY, not in parallel. Each status change triggers a
      // full kanban-projection rebuild server-side; firing them concurrently
      // (Promise.all) races those rebuilds across API workers, and a rebuild
      // from an older snapshot can overwrite a fresher one — leaving
      // already-sent invoices lingering in the Approved lane. Awaiting each in
      // turn means request N+1 starts only after N has committed, so the final
      // projection reflects every send.
      const results = [];
      for (const id of standardIds) {
        try {
          await updateInvoiceStatus(id, 'submitted_to_ap');
          results.push({ status: 'fulfilled' });
        } catch (err) {
          results.push({ status: 'rejected', reason: err });
        }
      }
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (failed > 0) {
        setErrorToast(
          `${failed} invoice${failed === 1 ? '' : 's'} could not be sent to AP.`
        );
        setTimeout(() => setErrorToast(null), 6000);
      }
      if (ok > 0) {
        setSentToast({
          count: ok,
          total: standardTotal,
          currency: dominantCurrency,
        });
        setTimeout(() => setSentToast(null), 4000);
      }
      // Drop the standard ids we just submitted from the selection map.
      // (VAT/Lux are no longer selectable from the Approved lane — they
      // surface as package cards driven by the dedicated dashboards.)
      setSelection((s) => {
        const next = { ...s };
        standardIds.forEach((id) => delete next[id]);
        return next;
      });
      loadData();
    } finally {
      setBusyBulk(false);
    }
  };

  // ----- Send for approval -----
  // Approver is the project owner (invoice.owner_id is set by the
  // classification step to the owning project's owner). Single click,
  // no dropdown — matches the old card-level action.

  const handleSendForApproval = async (invoice) => {
    if (!invoice.owner_id) {
      setErrorToast('No project owner set — classify this invoice first.');
      setTimeout(() => setErrorToast(null), 4000);
      return;
    }
    setSendingApprovalId(invoice.id);
    try {
      await createApprovalRequest(invoice.id, invoice.owner_id);
      loadData();
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || 'Failed to send for approval';
      setErrorToast(msg);
      setTimeout(() => setErrorToast(null), 4000);
    } finally {
      setSendingApprovalId(null);
    }
  };

  // ----- Batch as Luxembourg (confirm gate, replaces approval for Lux) -----

  const handleBatchAsLuxembourg = async (invoice) => {
    setBatchingLuxId(invoice.id);
    try {
      await batchLuxembourg(invoice.id);
      loadData();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.message ||
        'Failed to batch as Luxembourg';
      setErrorToast(msg);
      setTimeout(() => setErrorToast(null), 4000);
    } finally {
      setBatchingLuxId(null);
    }
  };

  const sendForApprovalContext = {
    role: currentUser?.role,
    eligibleStatuses: ['received', 'under_review'],
    onSend: handleSendForApproval,
    pending: sendingApprovalId,
    onBatchLux: handleBatchAsLuxembourg,
    batchingLux: batchingLuxId,
  };

  // ----- Upload -----

  const handleUpload = async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    if (fileList.length === 0) return;
    setUploading(true);
    const failures = [];
    try {
      // Upload sequentially so the server isn't pummeled and SSE updates feel
      // ordered. Per-file failures are collected and surfaced after the batch.
      for (const file of fileList) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          await uploadInvoice(formData);
        } catch (err) {
          const detail = err.response?.data?.detail;
          let msg;
          if (
            err.response?.status === 409 &&
            detail &&
            typeof detail === 'object' &&
            detail.error === 'duplicate_upload'
          ) {
            const existingId = detail.existing_invoice_id || '';
            const shortId = existingId.slice(0, 8);
            msg = `already on record as invoice ${shortId} — ignored`;
          } else if (typeof detail === 'string') {
            msg = detail;
          } else {
            msg = err.message || 'Upload failed';
          }
          failures.push(`${file.name}: ${msg}`);
        }
      }
      loadData();
      if (failures.length) {
        setErrorToast(failures.join(' · '));
        setTimeout(() => setErrorToast(null), 5000);
      }
    } finally {
      setUploading(false);
    }
  };

  const openInvoice = (inv) => navigate(`/invoice/${inv.id}`);

  const handleClearAll = async () => {
    const confirmed = window.confirm(
      'Delete every invoice? This wipes all cards from the board and cannot be undone.'
    );
    if (!confirmed) return;
    setClearing(true);
    try {
      await clearAllInvoices();
      setSelection({});
      loadData();
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || 'Clear all failed';
      setErrorToast(msg);
      setTimeout(() => setErrorToast(null), 4000);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: TOKENS.bg,
        fontFamily: UI_FONT,
        color: TOKENS.ink,
      }}
    >
      <style>{`
        @keyframes erbi-spin { to { transform: rotate(360deg); } }
        @keyframes erbi-card-arrive {
          0% {
            border-color: var(--erbi-arrive-accent);
            box-shadow:
              0 0 0 1px var(--erbi-arrive-accent),
              0 6px 24px -8px var(--erbi-arrive-accent),
              inset 0 0 0 1px var(--erbi-arrive-accent-soft);
            background: var(--erbi-arrive-accent-soft);
          }
          18% {
            border-color: var(--erbi-arrive-accent);
            box-shadow:
              0 0 0 1px var(--erbi-arrive-accent),
              0 4px 18px -8px var(--erbi-arrive-accent),
              inset 0 0 0 1px var(--erbi-arrive-accent-soft);
          }
          100% {
            border-color: ${TOKENS.border};
            box-shadow: none;
            background: ${TOKENS.panel};
          }
        }
      `}</style>
      <NavHeader
        endActions={
          <DeleteAllInvoicesButton
            onClick={handleClearAll}
            busy={clearing}
          />
        }
      />

      {loading ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: TOKENS.ink3,
          }}
        >
          Loading…
        </div>
      ) : error ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: TOKENS.danger,
          }}
        >
          {error}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '10px 18px 22px',
            alignItems: 'stretch',
            minHeight: 'calc(100vh - 52px - 64px)',
          }}
        >
          {LANES.map((lane) => {
            if (lane.id === 'approved') {
              return (
                <ApprovedLane
                  key="approved"
                  lane={lane}
                  standardItems={approvedStandardItems}
                  vatPackages={approvedVatPackages}
                  luxPackages={approvedLuxPackages}
                  childInvoiceLookup={childInvoiceLookup}
                  selection={selection}
                  toggleSelect={toggleSelect}
                  selectAllStandard={selectAllStandardApproved}
                  onCard={openInvoice}
                  recentlyMovedIds={recentlyMovedIds}
                  onToggleVatChecklistItem={toggleVatChecklist}
                  onToggleLuxChecklistItem={toggleLuxChecklist}
                />
              );
            }
            if (lane.id === 'pending_approval') {
              return (
                <PendingApprovalLane
                  key="pending_approval"
                  lane={lane}
                  standardItems={invoicesByLane.pending_approval || []}
                  vatPackages={vatPackages}
                  luxPackages={luxPackages}
                  childInvoiceLookup={childInvoiceLookup}
                  onCard={openInvoice}
                  recentlyMovedIds={recentlyMovedIds}
                  sendForApprovalContext={sendForApprovalContext}
                  onToggleVatChecklistItem={toggleVatChecklist}
                  onToggleLuxChecklistItem={toggleLuxChecklist}
                  onApproveVatPackage={approveVat}
                  onApproveLuxPackage={sendLuxToAp}
                />
              );
            }
            return (
              <Lane
                key={lane.id}
                lane={lane}
                items={invoicesByLane[lane.id]}
                onCard={openInvoice}
                sendForApprovalContext={sendForApprovalContext}
                recentlyMovedIds={recentlyMovedIds}
                dropZone={
                  lane.id === 'received'
                    ? { onFiles: handleUpload, uploading }
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      <BulkActionBar
        totalCount={selectedInvoices.length}
        standardCount={standardSelected.length}
        vatCount={vatSelected.length}
        luxCount={luxSelected.length}
        totalAmount={standardTotal}
        currency={dominantCurrency}
        onClear={() => setSelection({})}
        onSend={handleSend}
        busy={busyBulk}
        onOpenVatDashboard={() => navigate('/vat')}
        onOpenLuxDashboard={() => navigate('/luxembourg')}
      />
      <SentToast toast={sentToast} onDismiss={() => setSentToast(null)} />
      {errorToast && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: 22,
            transform: 'translateX(-50%)',
            zIndex: 45,
            background: TOKENS.danger,
            color: '#fff',
            borderRadius: 10,
            padding: '10px 16px',
            fontFamily: UI_FONT,
            fontSize: 12,
            boxShadow: '0 18px 48px rgba(60,10,10,0.35)',
          }}
        >
          {errorToast}
        </div>
      )}
    </div>
  );
}

export default KanbanBoard;
